// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from 'react';
import { Alert, Badge, Button, Card, Group, Select, Stack, Text, Title } from '@mantine/core';

import { ApiError, api } from '../../api/client';
import { devicesApi } from '../../api/devices';
import type { PortStatusResult } from '../../api/client';

/**
 * The smallest real control surface: one port, three buttons, one answer.
 *
 * WHAT THIS IS FOR: proving the first physical switch operation end to end —
 *   Server, Gateway, driver, SSH, real hardware, and back onto the screen.
 * WHAT IT IS NOT: the Port Manager. There is no faceplate, no multi-select, no
 *   PoE and no VLANs, deliberately. A graphical switch page built on an
 *   operations model that has never touched a device would be a drawing.
 *
 * # WHY THE DROPDOWN STOPS AT 48
 *
 * The GWN7806P shows 54 ports and 49–54 look like SFP/SFP+. Nobody has read
 * their CLI identifiers from the device, and the copper form cannot simply be
 * assumed to extend — that assumption is what produced a parser which could not
 * read a real table. The copper slice is what has been confirmed.
 *
 * # WHY THE BROWSER SENDS A NUMBER
 *
 * Because `Ethernet 1/0/5` and `eth1/0/5` are this vendor's words for the same
 * port, one for commands and one for output. A browser that knew either would be
 * a browser that knows CLI syntax, and the next vendor would need a second
 * browser. The number is what everything above the driver agrees on.
 */
export function PortControl({ deviceId }: { deviceId: string }) {
  const [port, setPort] = useState<string | null>('5');
  const [busy, setBusy] = useState<null | 'status' | 'on' | 'off'>(null);
  const [result, setResult] = useState<PortStatusResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const readStatus = async () => {
    if (!port) return;
    // ONE OPERATION AT A TIME. The per-device Gate on the Gateway is the
    // authority — this only stops the obvious double-click, and says so rather
    // than pretending to be the control.
    setBusy('status');
    setError(null);
    try {
      setResult(await api.devicePortStatus(deviceId, Number(port)));
    } catch (err) {
      setError(err as ApiError);
      setResult(null);
    } finally {
      setBusy(null);
    }
  };

  // ONE TYPED WRITE, THEN ITS OWN INDEPENDENT VERIFICATION.
  //
  // The Gateway does the work: a fresh session to read the port, a fresh session
  // to change it, and a THIRD fresh session to look again. This only asks.
  //
  // The outcome is the answer, including UNKNOWN. A change that may have landed
  // and cannot be confirmed is never retried automatically — that is the whole
  // reason the three states exist, and the button does not quietly try again.
  const setState = async (enabled: boolean) => {
    if (!port) return;
    setBusy(enabled ? 'on' : 'off');
    setError(null);
    try {
      const written = await devicesApi.setPortState(deviceId, Number(port), enabled);
      // Show what the WRITE concluded, then read the port again so the box
      // reflects the device rather than the request.
      if (written.outcome === 'SUCCESS') {
        setResult(await api.devicePortStatus(deviceId, Number(port)));
      } else {
        setResult({
          outcome: written.outcome,
          detail: written.detail,
          port: Number(port),
          state: { port: Number(port), admin_state: 'UNKNOWN', link_state: 'UNKNOWN' },
        });
      }
    } catch (err) {
      setError(err as ApiError);
      setResult(null);
    } finally {
      setBusy(null);
    }
  };

  const working = busy !== null;

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>Port control</Title>

        <Group align="flex-end" gap="sm">
          <Select
            label="Port"
            data={copperPorts()}
            value={port}
            onChange={setPort}
            w={120}
            searchable
            disabled={working}
          />
          <Button onClick={() => void readStatus()} loading={busy === 'status'} disabled={working}>
            Status
          </Button>
          {/* ENABLED AS OF 2026-08-22, once Meshvara had read this switch
              through the whole path. Every change is verified by an independent
              read before it is called a success. */}
          <Button
            color="green"
            variant="light"
            onClick={() => void setState(true)}
            loading={busy === 'on'}
            disabled={working}
          >
            Turn On
          </Button>
          <Button
            color="red"
            variant="light"
            onClick={() => void setState(false)}
            loading={busy === 'off'}
            disabled={working}
          >
            Turn Off
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          Every change is applied in its own session and then confirmed by
          reading the port back over a separate one.
        </Text>

        {working && (
          <Text size="sm" c="dimmed">
            {busy === 'status'
              ? 'Working — opening a fresh session, logging in, reading the port…'
              : 'Working — applying the change, then verifying it over a new session…'}
          </Text>
        )}

        {error && (
          <Alert color="red" title="Could not read the port">
            {error.message}
          </Alert>
        )}

        {result && <PortStatusBox result={result} />}
      </Stack>
    </Card>
  );
}

/**
 * The answer, in Meshvara's words.
 *
 * # WHY THE VENDOR'S WORD IS SMALL AND SECONDARY
 *
 * `notconnect` means administratively enabled with no link. An operator should
 * not have to know that, and the next vendor will spell it differently. The
 * normalized state is the answer; the device's own word sits underneath it so
 * somebody comparing this against a switch CLI can line the two up.
 */
function PortStatusBox({ result }: { result: PortStatusResult }) {
  const { state } = result;

  // AN OUTCOME THAT IS NOT SUCCESS IS NOT A PORT STATE. Showing "UNKNOWN —
  // UNKNOWN" beside a green tick would read as an answer; it is the absence of
  // one.
  if (result.outcome !== 'SUCCESS') {
    return (
      <Alert color={result.outcome === 'UNKNOWN' ? 'yellow' : 'red'} title={`Result: ${result.outcome}`}>
        {result.detail || 'The device did not answer with a state.'}
        {result.ssh_state && (
          <Text size="xs" c="dimmed" mt="xs">
            Transport: {result.ssh_state}
          </Text>
        )}
      </Alert>
    );
  }

  return (
    <Card withBorder padding="sm" bg="var(--mantine-color-default-hover)">
      <Group gap="xs">
        <Text size="sm" fw={500}>
          Status
        </Text>
        <Badge color={adminColour(state.admin_state)} size="lg">
          {describe(state.admin_state, state.link_state)}
        </Badge>
      </Group>
      {state.vendor_interface && (
        <Text size="xs" c="dimmed" mt={6}>
          Port {state.port} · device calls it {state.vendor_interface}
        </Text>
      )}
    </Card>
  );
}

/**
 * The normalized state as one phrase.
 *
 * Only states that have been observed or typed get words. Anything else says so
 * — a display that invented a plausible phrase for a value the driver could not
 * interpret would be the parser's honesty thrown away at the last step.
 */
function describe(admin: string, link: string): string {
  if (admin === 'DISABLED') return 'Disabled';
  if (admin === 'ENABLED') {
    if (link === 'UP') return 'Enabled — Link Up';
    if (link === 'DOWN') return 'Enabled — Link Down';
    return 'Enabled';
  }
  return 'Unknown';
}

function adminColour(admin: string): string {
  if (admin === 'ENABLED') return 'green';
  if (admin === 'DISABLED') return 'gray';
  return 'yellow';
}

/** Copper ports 1–48. See the note at the top about 49–54. */
function copperPorts(): string[] {
  return Array.from({ length: 48 }, (_, i) => String(i + 1));
}
